import {
    FeatureFlagIdAccessConflicts,
    FeatureFlagIdAccessExplorer,
    FeatureFlagIdAccessRequest,
    FeatureFlagIdAiTicketing,
    FeatureFlagIdCopilotThomas,
    FeatureFlagIdExploreSearchV2,
    FeatureFlagIdExternalTicketing,
    FeatureFlagIdFunctions,
    FeatureFlagIdInventory,
    FeatureFlagIdOffboarding,
    FeatureFlagIdPasswordPush,
    FeatureFlagIdResourceActionGroupUi,
    FeatureFlagIdStepUpAuth,
    FeatureFlagIdTsdbMetricsDashboards,
    FeatureFlagIdWorkflowsFeatureV2,
} from 'c1gen/features'
import {
    AccessconflictV1AccessConflictServiceowner,
    AccessreviewV1AccessReviewSearchServiceeditor,
    AccessreviewV1AccessReviewSearchServiceowner,
    AppV1Appseditor,
    AppV1Appsowner,
    AppV1ConnectorServiceeditor,
    AppV1ConnectorServiceowner,
    AutomationsV1AutomationServiceowner,
    DirectoryV1DirectoryServiceeditor,
    DirectoryV1DirectoryServiceowner,
    ExternalTicketsV1ExternalTicketseditor,
    ExternalTicketsV1ExternalTicketsowner,
    FunctionsV1FunctionsServiceowner,
    IntegrationDatasourceV1DataSourceCatalogServiceeditor,
    IntegrationDatasourceV1DataSourceCatalogServiceowner,
    OffboardingV1OffboardingSearchServiceowner,
    PasswordPushV1PasswordPushFlowServiceowner,
    PolicyV1Policieseditor,
    PolicyV1Policiesowner,
    RequestcatalogV1RequestCatalogManagementServiceeditor,
    RequestcatalogV1RequestCatalogManagementServiceowner,
    StepupV1StepUpProviderServiceeditor,
    StepupV1StepUpProviderServiceowner,
    TaskV1TaskActionsServiceowner,
    TenantV1Tenantowner,
    TsdbV1TsdbmetricsServiceowner,
    TsdbV1TsdbmetricsServiceviewer,
    UserV1AgentServiceowner,
    UserV1UserSearcheditor,
    UserV1UserSearchowner,
    WebhooksV1WebhooksServiceeditor,
    WebhooksV1WebhooksServiceowner,
} from 'c1gen/roles'
import { AppsSidebarOverlay } from 'components/modular-nav/AppsSidebarOverlay'
import { SettingsSidebarOverlay } from 'components/modular-nav/SettingsSidebarOverlay'
import { NavContainer } from 'components/modular-nav/types'
import { SystemManagementWarningIndicator } from 'components/pages/admin/settings/system-management/SystemManagementWarningIndicator'
import { routes } from 'lib/routes'
import {
    Bot,
    Cable,
    ChartSpline,
    CircleOff,
    GitFork,
    CircleUser,
    ClipboardList,
    Clock,
    Compass,
    Contact,
    FileCode2,
    Fingerprint,
    Flag,
    HardDrive,
    Headphones,
    Key,
    LayoutGrid,
    ListChecks,
    Package,
    RectangleEllipsis,
    Scale,
    ScrollText,
    Search,
    Send,
    Settings,
    ShieldCheck,
    ShoppingCart,
    Ticket,
    User,
    UserCog,
    UserMinus,
    Users,
    Webhook,
    Zap,
} from 'lucide-react'
import { FormattedMessage } from 'react-intl'

/**
 * Navigation configuration for the new nav (NEW_NAV_2026 feature flag)
 *
 * Structure: Container > Module
 * - Containers are top-level groupings shown in the primary sidebar
 * - Modules are features within containers shown in flyout menus
 *
 * Note: `count` values are injected at runtime from DashboardContext
 */
export const navigationConfig: NavContainer[] = [
    {
        id: 'my-work',
        label: <FormattedMessage id="nav_my_work" defaultMessage="My work" />,
        icon: ClipboardList,
        modules: [
            {
                id: 'requests',
                label: <FormattedMessage id="nav_requests" defaultMessage="Requests" />,
                icon: Send,
                pathname: routes.requests.assignedToMe,
                selectedPaths: [routes.requests.list, routes.requests.create],
            },
            {
                id: 'reviews',
                label: <FormattedMessage id="nav_reviews" defaultMessage="Reviews" />,
                icon: ListChecks,
                pathname: routes.assignments.reviews.list,
            },
        ],
    },
    {
        id: 'access',
        label: <FormattedMessage id="nav_access" defaultMessage="Access" />,
        icon: Key,
        modules: [
            {
                id: 'browse-access',
                label: <FormattedMessage id="nav_browse_access" defaultMessage="Browse" />,
                icon: Search,
                pathname: routes.requests.appCatalog,
                features: [FeatureFlagIdAccessRequest],
            },
            {
                id: 'expiring-access',
                label: <FormattedMessage id="nav_expiring_access" defaultMessage="Expiring" />,
                icon: Clock,
                pathname: routes.requests.temporary,
                features: [FeatureFlagIdAccessRequest],
            },
            {
                id: 'accounts-access',
                label: <FormattedMessage id="nav_accounts_access" defaultMessage="Accounts" />,
                icon: CircleUser,
                pathname: routes.requests.accounts,
                features: [FeatureFlagIdAccessRequest],
            },
            {
                id: 'passwords-access',
                label: <FormattedMessage id="nav_passwords_access" defaultMessage="Passwords" />,
                icon: RectangleEllipsis,
                pathname: routes.passwordReset.index,
                features: [FeatureFlagIdPasswordPush],
            },
            {
                id: 'request-access',
                label: <FormattedMessage id="nav_request_access" defaultMessage="Request" />,
                icon: ShoppingCart,
                pathname: routes.requests.create,
                features: [FeatureFlagIdAccessRequest],
                separatorBefore: true,
            },
        ],
    },
    {
        id: 'apps',
        separatorBefore: true,
        label: <FormattedMessage id="nav_apps" defaultMessage="Apps" />,
        icon: LayoutGrid,
        customFlyout: AppsSidebarOverlay,
        roles: [AppV1Appseditor, AppV1Appsowner],
        modules: [
            {
                id: 'applications',
                label: <FormattedMessage id="nav_applications" defaultMessage="Applications" />,
                icon: LayoutGrid,
                pathname: routes.applications.list,
                roles: [AppV1Appseditor, AppV1Appsowner],
            },
        ],
    },
    {
        id: 'groups',
        label: <FormattedMessage id="nav_groups" defaultMessage="Groups" />,
        icon: Users,
        features: [FeatureFlagIdResourceActionGroupUi],
        modules: [
            {
                id: 'groups-i-manage',
                label: <FormattedMessage id="nav_groups_i_manage" defaultMessage="Groups I manage" />,
                icon: UserCog,
                pathname: routes.groups.managerOf,
                features: [FeatureFlagIdResourceActionGroupUi],
            },
            {
                id: 'groups-im-in',
                label: <FormattedMessage id="nav_groups_im_in" defaultMessage="Groups I'm in" />,
                icon: User,
                pathname: routes.groups.memberOf,
                features: [FeatureFlagIdResourceActionGroupUi],
            },
        ],
    },
    {
        id: 'explore',
        label: <FormattedMessage id="nav_explore" defaultMessage="Explore" />,
        icon: Compass,
        modules: [
            {
                id: 'analytics',
                label: <FormattedMessage id="nav_analytics" defaultMessage="Analytics" />,
                icon: ChartSpline,
                pathname: routes.analytics,
                roles: [TsdbV1TsdbmetricsServiceviewer, TsdbV1TsdbmetricsServiceowner],
                features: [FeatureFlagIdTsdbMetricsDashboards],
            },
            {
                id: 'inventory',
                label: <FormattedMessage id="nav_inventory" defaultMessage="Inventory" />,
                icon: Package,
                pathname: routes.explore.inventory,
                roles: [TenantV1Tenantowner],
                features: [FeatureFlagIdInventory],
            },
            {
                id: 'search',
                label: <FormattedMessage id="nav_search" defaultMessage="Search" />,
                icon: Search,
                pathname: routes.explore.search,
                roles: [TenantV1Tenantowner],
                features: [FeatureFlagIdExploreSearchV2],
            },
            {
                id: 'graph',
                label: <FormattedMessage id="nav_graph" defaultMessage="Graph" />,
                icon: GitFork,
                pathname: routes.explore.graph,
                roles: [TenantV1Tenantowner],
                features: [FeatureFlagIdAccessExplorer],
            },
            {
                id: 'task-log',
                label: <FormattedMessage id="nav_task_log" defaultMessage="Task log" />,
                icon: ScrollText,
                pathname: routes.taskLog,
                roles: [TaskV1TaskActionsServiceowner],
            },
        ],
    },
    {
        id: 'governance',
        label: <FormattedMessage id="nav_governance" defaultMessage="Governance" />,
        icon: ShieldCheck,
        modules: [
            {
                id: 'campaigns',
                label: <FormattedMessage id="nav_campaigns" defaultMessage="Campaigns" />,
                icon: Flag,
                pathname: routes.campaigns.list,
                roles: [
                    AccessreviewV1AccessReviewSearchServiceeditor,
                    AccessreviewV1AccessReviewSearchServiceowner,
                ],
            },
            {
                id: 'access-conflicts',
                label: <FormattedMessage id="nav_access_conflicts" defaultMessage="Access conflicts" />,
                icon: CircleOff,
                pathname: routes.conflictMonitors.list,
                roles: [AccessconflictV1AccessConflictServiceowner],
                features: [FeatureFlagIdAccessConflicts],
                // count injected from DashboardContext at runtime
            },
            {
                id: 'access-profiles',
                label: <FormattedMessage id="nav_access_profiles" defaultMessage="Access profiles" />,
                icon: Contact,
                pathname: routes.accessProfiles.list,
                roles: [
                    RequestcatalogV1RequestCatalogManagementServiceeditor,
                    RequestcatalogV1RequestCatalogManagementServiceowner,
                ],
                features: [FeatureFlagIdAccessRequest],
            },
            {
                id: 'policies',
                label: <FormattedMessage id="nav_policies" defaultMessage="Policies" />,
                icon: Scale,
                pathname: routes.settings.policies.list,
                roles: [PolicyV1Policieseditor, PolicyV1Policiesowner],
            },
        ],
    },
    {
        id: 'directory',
        label: <FormattedMessage id="nav_directory" defaultMessage="Directory" />,
        icon: User,
        modules: [
            {
                id: 'users',
                label: <FormattedMessage id="nav_users" defaultMessage="Users" />,
                icon: User,
                pathname: routes.users.list,
                roles: [UserV1UserSearcheditor, UserV1UserSearchowner],
            },
            {
                id: 'directory-groups',
                label: <FormattedMessage id="nav_directory_groups" defaultMessage="Groups" />,
                icon: Users,
                pathname: routes.directory.groups.list,
                roles: [UserV1UserSearcheditor, UserV1UserSearchowner],
            },
            {
                id: 'user-data-sources',
                label: <FormattedMessage id="nav_user_data_sources" defaultMessage="User data sources" />,
                icon: UserCog,
                pathname: routes.userDataSources.list,
                roles: [DirectoryV1DirectoryServiceeditor, DirectoryV1DirectoryServiceowner],
            },
        ],
    },
    {
        id: 'workflows',
        label: <FormattedMessage id="nav_workflows" defaultMessage="Workflows" />,
        icon: Zap,
        modules: [
            {
                id: 'automations',
                label: <FormattedMessage id="nav_automations" defaultMessage="Automations" />,
                icon: Zap,
                pathname: routes.automations.list,
                roles: [AutomationsV1AutomationServiceowner],
                features: [FeatureFlagIdWorkflowsFeatureV2],
            },
            {
                id: 'functions',
                label: <FormattedMessage id="nav_functions" defaultMessage="Functions" />,
                icon: FileCode2,
                pathname: routes.settings.functions.list,
                roles: [FunctionsV1FunctionsServiceowner],
                features: [FeatureFlagIdFunctions],
            },
            {
                id: 'offboarding',
                label: <FormattedMessage id="nav_offboarding" defaultMessage="Offboarding" />,
                icon: UserMinus,
                pathname: routes.offboarding.list,
                roles: [OffboardingV1OffboardingSearchServiceowner],
                features: [FeatureFlagIdOffboarding],
            },
        ],
    },
    {
        id: 'integrations',
        label: <FormattedMessage id="nav_integrations" defaultMessage="Integrations" />,
        icon: Cable,
        modules: [
            {
                id: 'connectors',
                label: <FormattedMessage id="nav_connectors" defaultMessage="Connectors" />,
                icon: Cable,
                pathname: routes.connectors.list,
                roles: [AppV1ConnectorServiceeditor, AppV1ConnectorServiceowner],
            },
            {
                id: 'webhooks',
                label: <FormattedMessage id="nav_webhooks" defaultMessage="Webhooks" />,
                icon: Webhook,
                pathname: routes.settings.webhooks,
                roles: [WebhooksV1WebhooksServiceeditor, WebhooksV1WebhooksServiceowner],
            },
            {
                id: 'service-desk',
                label: <FormattedMessage id="nav_service_desk" defaultMessage="Service desk" />,
                icon: Headphones,
                pathname: routes.settings.serviceDeskIntegration.list,
                roles: [TenantV1Tenantowner],
                features: [FeatureFlagIdAiTicketing],
            },
            {
                id: 'external-ticketing',
                label: <FormattedMessage id="nav_external_ticketing" defaultMessage="External ticketing" />,
                icon: Ticket,
                pathname: routes.settings.externalTicketing.list,
                roles: [ExternalTicketsV1ExternalTicketseditor, ExternalTicketsV1ExternalTicketsowner],
                features: [FeatureFlagIdExternalTicketing],
            },
            {
                id: 'external-data-sources',
                label: <FormattedMessage id="nav_external_data_sources" defaultMessage="External data sources" />,
                icon: HardDrive,
                pathname: routes.settings.externalDataSources,
                roles: [IntegrationDatasourceV1DataSourceCatalogServiceeditor, IntegrationDatasourceV1DataSourceCatalogServiceowner],
            },
            {
                id: 'step-up-authenticators',
                label: <FormattedMessage id="nav_step_up_authenticators" defaultMessage="Step-up auth" />,
                icon: Fingerprint,
                pathname: routes.settings.stepUp,
                roles: [StepupV1StepUpProviderServiceeditor, StepupV1StepUpProviderServiceowner],
                features: [FeatureFlagIdStepUpAuth],
            },
            {
                id: 'passwords',
                label: <FormattedMessage id="nav_passwords" defaultMessage="Passwords" />,
                icon: RectangleEllipsis,
                pathname: routes.settings.passwords.list,
                roles: [PasswordPushV1PasswordPushFlowServiceowner],
                features: [FeatureFlagIdPasswordPush],
            },
        ],
    },
    {
        id: 'agentic-ai',
        label: <FormattedMessage id="nav_agentic_ai" defaultMessage="Agentic AI" />,
        icon: Bot,
        modules: [
            {
                id: 'agents',
                label: <FormattedMessage id="nav_agents" defaultMessage="Agents" />,
                icon: Bot,
                pathname: routes.settings.agenticAi.list,
                roles: [UserV1AgentServiceowner],
                features: [FeatureFlagIdCopilotThomas],
            },
        ],
    },
    {
        id: 'settings',
        label: <FormattedMessage id="nav_settings" defaultMessage="Settings" />,
        icon: Settings,
        customFlyout: SettingsSidebarOverlay,
        customIndicator: SystemManagementWarningIndicator,
        persistentPaths: ['/admin/settings'],
        roles: [TenantV1Tenantowner],
        modules: [],
        defaultPath: routes.settings.organization,
    },
]
